<script lang="ts">
  // Zugeklappte Abschnitte im Claude-Baum: Symbole und Füllbalken statt
  // Text (Tim 19.10.2026: „Wenn die Fenster hier zugeklappt werden, dann
  // auch mit Symbolen und Balken mit Füllstand wechseln. Das ist dann
  // besser als Text."). Der Satz dazu steht weiter als aria-label am Kopf
  // (Explorer.svelte); Farbe nur für Zustand (docs/gestaltung.md).
  import Icon from "../Icon.svelte";
  import { getClaude } from "../claude-summary.svelte";
  import { getGedaechtnis } from "../gedaechtnis-summary.svelte";
  import { zugriffeErklaerung } from "../gedaechtnis/gedaechtnis";
  import { gezeigtesRepo } from "./baum.svelte";
  import FensterMini from "./FensterMini.svelte";
  import { abgemeldet, repoStandFuer, wannKurz, type Fenster } from "./claude";
  import { tageslimitJetzt } from "./tageslimit.svelte";

  let { art }: { art: "chats" | "aenderungen" | "commits" | "verbrauch" | "gedaechtnis" } = $props();

  // Gedächtnis (Tim 21.10.2026: „Wenn minimiert, dann sind hier die Zugriffe
  // auf die Datenbank lesen/schreiben zu sehen, wenn sich etwas ändert"):
  // Lupe = gelesen, Stift = geschrieben, je die Zahl der letzten Stunde.
  // Kommt ein Zugriff dazu, leuchtet sein Symbol kurz auf -- neutral, eine
  // Bewegung ist kein Zustand.
  const gd = $derived(art === "gedaechtnis" ? getGedaechtnis() : undefined);
  // Was die Zahlen sagen (Tim: „Was sagen die Zahlen genau?") -- im Tooltip;
  // die Seite Gedächtnis schreibt es aus (Abschnitt Zugriffe).
  const erklaerung = $derived(zugriffeErklaerung(gd?.zugriffe));
  let blitzLesen = $state(false);
  let blitzSchreiben = $state(false);
  let vorLesen: string | undefined;
  let vorSchreiben: string | undefined;
  function blitze(neu: string | undefined, vor: string | undefined, setze: (b: boolean) => void): string | undefined {
    if (neu && vor !== undefined && neu !== vor) {
      setze(true);
      setTimeout(() => setze(false), 2500);
    }
    return neu ?? vor ?? "";
  }
  $effect(() => {
    const z = gd?.zugriffe;
    if (!z) return;
    vorLesen = blitze(z.letzter_lesen, vorLesen, (b) => (blitzLesen = b));
    vorSchreiben = blitze(z.letzter_schreiben, vorSchreiben, (b) => (blitzSchreiben = b));
  });

  const st = $derived(getClaude()?.stand);
  const rs = $derived(st ? repoStandFuer(st, gezeigtesRepo()) : undefined);
  const wartet = $derived(st?.chats.filter((c) => c.zustand === "wartet").length ?? 0);
  const arbeitet = $derived(st?.chats.filter((c) => c.zustand === "arbeitet").length ?? 0);

  let jetzt = $state(new Date());
  $effect(() => {
    const iv = setInterval(() => (jetzt = new Date()), 60_000);
    return () => clearInterval(iv);
  });

  // Verbrauch (Tim 01.11.2026: „zuerst das eigene Tageslimit, dann
  // Wochenlimit und dann Fable, je nachdem, wie viel Platz ist"): was nicht
  // passt, bricht weg (siehe unten), darum in dieser Reihenfolge.
  const tl = $derived(art === "verbrauch" ? tageslimitJetzt(st?.verbrauch, jetzt) : undefined);
  const balken = $derived<{ key: string; icon?: "clock" | "calendar" | "sun"; name?: string; f: Fenster | undefined }[]>([
    { key: "heute", icon: "sun", f: tl ? { anteil: tl.anteil } : undefined },
    { key: "fuenf", icon: "clock", f: st?.verbrauch.fuenf_stunden },
    { key: "woche", icon: "calendar", f: st?.verbrauch.woche },
    ...(st?.verbrauch.modelle ?? []).map((m) => ({ key: m.name, name: m.name, f: m as Fenster })),
  ]);
</script>

<span class="zu t-meta tabular-nums" aria-hidden="true">
  {#if art === "chats"}
    {#if abgemeldet(st?.anmeldung)}<span class="teil text-danger"><Icon name="alert-tri" size={12} /></span>{/if}
    {#if wartet}<span class="teil text-warning"><i class="punkt warn"></i>{wartet}</span>{/if}
    {#if arbeitet}<span class="teil text-laeuft"><i class="punkt laeuft"></i>{arbeitet}</span>{/if}
  {:else if art === "aenderungen" && rs}
    {#if rs.fehler}
      <span class="teil text-danger"><Icon name="alert-tri" size={12} /></span>
    {:else}
      {#if rs.voraus > 0}<span class="teil text-warning"><Icon name="upload" size={12} />{rs.voraus}</span>{/if}
      {#if rs.offen_zahl > 0}<span class="teil"><Icon name="file" size={12} />{rs.offen_zahl}</span>{/if}
      {#if rs.voraus === 0 && rs.offen_zahl === 0}<span class="teil text-success"><Icon name="check-circle" size={12} /></span>{/if}
    {/if}
  {:else if art === "commits" && rs}
    {#if rs.voraus > 0}<span class="teil text-warning"><Icon name="upload" size={12} />{rs.voraus}</span>{/if}
    {#if rs.verlauf?.[0]}<span class="teil"><Icon name="history" size={12} />{wannKurz(rs.verlauf[0].zeit, jetzt)}</span>{/if}
  {:else if art === "gedaechtnis"}
    {#if gd?.fehler}<span class="teil text-danger"><Icon name="alert-tri" size={12} /></span>{/if}
    <span class="teil zugriff" class:blitz={blitzLesen} title={erklaerung.lesen}><Icon name="search" size={12} />{gd?.zugriffe?.lesen ?? 0}</span>
    <span class="teil zugriff" class:blitz={blitzSchreiben} title={erklaerung.schreiben}><Icon name="pencil" size={12} />{gd?.zugriffe?.schreiben ?? 0}</span>
  {:else if art === "verbrauch"}
    {#each balken as b (b.key)}
      {#if b.f}<FensterMini f={b.f} icon={b.icon} name={b.name} {jetzt} zahl />{/if}
    {/each}
  {/if}
</span>

<style>
  /* Nichts anschneiden (Tim 19.10.2026: „Wenn der Platz nicht reicht,
     dann bleibt nur der aktuelle Verbrauch"): jede Angabe ist ein Block;
     passt einer nicht mehr in die Zeile, bricht er in eine zweite um, und
     die ist abgeschnitten -- ganz weg statt halb. Das Wichtigste steht
     darum vorn (Verbrauch: Heute vor Woche vor Fable). */
  .zu {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    column-gap: 0.65rem;
    row-gap: 6px;
    width: 100%;
    height: 1.15rem;
    overflow: hidden;
    color: var(--fg-muted);
  }
  .teil {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    flex: none;
    height: 1.15rem;
  }
  .punkt {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    display: inline-block;
  }
  .punkt.warn {
    background: var(--warning);
  }
  .punkt.laeuft {
    background: var(--color-laeuft);
    animation: laeuft-puls 1.8s ease-in-out infinite;
  }
  .zugriff {
    border-radius: 0.3rem;
    padding: 0 0.2rem;
    margin: 0 -0.2rem;
  }
  .zugriff.blitz {
    color: var(--fg);
    animation: zugriff-blitz 2.5s ease-out;
  }
  @keyframes zugriff-blitz {
    0% {
      background: color-mix(in oklab, var(--fg) 22%, transparent);
    }
    100% {
      background: transparent;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .punkt.laeuft,
    .zugriff.blitz {
      animation: none;
    }
  }
</style>
